import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Zap, Clock, Plug, ChevronRight, AlertCircle, Plus, Sparkles } from 'lucide-react';
import { pipelinesApi, type PipelineInstance } from '../../api/pipelinesApi';
import { haptic } from '../../telegram';

/*
  Отдельный экран «Мои сценарии». Отличие от ленты на главной:
  сценарии, связанные через «по завершении запустить автоматизацию»
  (config.on_complete_pipeline_id), рисуются одной цепочкой с связяющими узлами —
  также как на экране отдельного сценария.
*/

function triggerLabel(t: string): string {
  if (t === 'schedule') return 'Расписание';
  if (t === 'inbound_webhook') return 'Вебхук';
  return t || 'триггер';
}

function triggerIcon(t: string) {
  if (t === 'schedule') return <Clock />;
  if (t === 'inbound_webhook') return <Plug />;
  return <Zap />;
}

function chainTargetId(p: PipelineInstance): string | null {
  const v = p.config && (p.config as any).on_complete_pipeline_id;
  return typeof v === 'string' && v ? v : null;
}

interface Chain { items: PipelineInstance[]; }

function buildChains(pipes: PipelineInstance[]): Chain[] {
  const byId = new Map(pipes.map((p) => [p.id, p]));
  const incoming = new Set<string>();
  for (const p of pipes) {
    const t = chainTargetId(p);
    if (t && byId.has(t)) incoming.add(t);
  }
  const visited = new Set<string>();
  const chains: Chain[] = [];
  for (const p of pipes) {
    if (incoming.has(p.id) || visited.has(p.id)) continue;
    const items: PipelineInstance[] = [];
    const seen = new Set<string>();
    let cur: PipelineInstance | undefined = p;
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id); visited.add(cur.id); items.push(cur);
      const nextId = chainTargetId(cur);
      cur = nextId ? byId.get(nextId) : undefined;
    }
    chains.push({ items });
  }
  for (const p of pipes) {
    if (!visited.has(p.id)) { chains.push({ items: [p] }); visited.add(p.id); }
  }
  return chains;
}

const clickableStyle: React.CSSProperties = { cursor: 'pointer' };

export function MyAutomations() {
  const nav = useNavigate();
  const [pipes, setPipes] = useState<PipelineInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    pipelinesApi.list().then((r) => { setPipes(r || []); setLoading(false); }).catch(() => { setError(true); setLoading(false); });
  }, []);

  const chains = buildChains(pipes);

  return (
    <div className="rf-detail">
      <div className="rf-topbar">
        <button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button>
        <div className="rf-topbar-title">Мои сценарии</div>
        <button className="rf-back" onClick={() => { haptic('light'); nav('/automation/wizard'); }}><Sparkles />Создать</button>
      </div>
      <div className="rf-detail-scroll">
        {loading ? (
          <div className="rf-loading"><div className="rf-spin" /><p>Загрузка…</p></div>
        ) : error ? (
          <div className="rf-empty"><div className="rf-empty-ic"><AlertCircle /></div><h3>Не удалось загрузить</h3><p>Проверьте подключение и попробуйте снова</p></div>
        ) : pipes.length === 0 ? (
          <div className="rf-empty"><div className="rf-empty-ic"><Zap /></div><h3>Сценариев пока нет</h3><p>Создайте первый через оркестратор или вручную с главного экрана</p></div>
        ) : (
          <div className="rf-rows rf-stagger">
            {chains.map((chain, idx) => (
              chain.items.length === 1 ? (
                <div key={chain.items[0].id} className="rf-card rf-row" onClick={() => { haptic('light'); nav('/automation/' + chain.items[0].id); }}>
                  <div className="rf-row-ic">{triggerIcon(chain.items[0].trigger_type)}</div>
                  <div className="rf-row-body">
                    <div className="rf-row-title">{chain.items[0].name}</div>
                    <div className="rf-row-meta"><span><Zap />{triggerLabel(chain.items[0].trigger_type)}</span></div>
                  </div>
                  <div className="rf-row-right">
                    <ChevronRight />
                    <span className={'rf-status' + (chain.items[0].enabled ? ' ok' : '')}><span className="dot" />{chain.items[0].admin_paused ? '⏸ Приостановлена админом' : chain.items[0].enabled ? 'Активен' : 'Выкл'}</span>
                  </div>
                </div>
              ) : (
                <div key={'chain-' + idx} className="rf-card rf-cardpad">
                  <div className="rf-seclabel">Связанная цепочка · {chain.items.length} сценария</div>
                  <div className="rf-timeline">
                    {chain.items.map((p, i) => (
                      <div className="rf-tl" key={p.id} style={clickableStyle} onClick={() => { haptic('light'); nav('/automation/' + p.id); }}>
                        <div className="rf-tl-rail">
                          <div className={'rf-tl-dot' + (i === 0 ? ' accent' : '')}>{triggerIcon(p.trigger_type)}</div>
                          {i < chain.items.length - 1 && <div className="rf-tl-line" />}
                        </div>
                        <div className="rf-tl-body">
                          <div className="rf-tl-label">{i === 0 ? triggerLabel(p.trigger_type).toUpperCase() : 'ПО ЗАВЕРШЕНИИ Предыдущего'}</div>
                          <div className="rf-tl-title">{p.name}</div>
                          <div className="rf-tl-desc"><span className={'rf-status' + (p.enabled ? ' ok' : '')}><span className="dot" />{p.admin_paused ? '⏸ Приостановлена администрацией' : p.enabled ? 'Активен' : 'Выключен'}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
